/**
 * 지원 접근 이력 (고객사 화면)
 * ---------------------------------------------------------------------------
 * 플랫폼 운영자가 우리 회사 데이터에 언제·왜 접근했는지 고객사가 직접 본다.
 *
 * 화면을 만들 때 신경 쓴 것
 *   - 기록이 없는 상태가 **좋은 상태**다. 빈 화면을 오류처럼 보이게 하지 않는다.
 *   - "조회만 했는지, 무언가를 바꿨는지"를 가장 먼저 보여준다.
 *     고객이 실제로 알고 싶은 건 그것이다.
 *   - 진행 중인 세션은 눈에 띄게 표시한다. 지금 누가 보고 있다는 사실이
 *     지난 기록보다 중요하다.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Empty, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText, qk } from '../../lib/queries';

const { Text, Paragraph } = Typography;

interface Activity {
  action: string;
  label: string;
  count: number;
  isChange: boolean;
}

export interface SupportAccessRecord {
  supportSessionId: string;
  operatorName: string | null;
  operatorEmail: string | null;
  reason: string;
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
  endReason: string | null;
  accessCount: number;
  changeCount: number;
  activities: Activity[];
  active: boolean;
}

export default function SupportAccessPanel({ accessAllowed }: { accessAllowed?: boolean }) {
  const client = useQueryClient();

  const history = useQuery<SupportAccessRecord[], NormalizedError>({
    queryKey: qk.supportAccess,
    queryFn: async () =>
      (await apiClient.get<SupportAccessRecord[]>('/tenant/support-access')).data,
    // 진행 중인 세션이 있을 수 있으므로 오래 캐시하지 않는다.
    staleTime: 30 * 1000,
  });

  const rows = history.data ?? [];
  const activeCount = rows.filter((r) => r.active).length;

  const columns: ColumnsType<SupportAccessRecord> = [
    {
      title: '접근 일시',
      key: 'when',
      width: 200,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>{formatDateTime(row.startedAt)}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.endedAt ? `~ ${formatTime(row.endedAt)} (${duration(row)})` : '진행 중'}
          </Text>
        </Space>
      ),
    },
    {
      title: '담당자',
      key: 'operator',
      width: 200,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>{row.operatorName ?? <Text type="secondary">확인 불가</Text>}</span>
          {row.operatorEmail && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.operatorEmail}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '사유',
      dataIndex: 'reason',
      key: 'reason',
      render: (v: string) => <Text>{v}</Text>,
    },
    {
      title: '처리 내역',
      key: 'activity',
      width: 180,
      render: (_, row) => (
        <Space size={4} wrap>
          <Tag>조회 {row.accessCount}건</Tag>
          {row.changeCount > 0 ? (
            <Tooltip
              title={row.activities
                .filter((a) => a.isChange)
                .map((a) => `${a.label} ${a.count}건`)
                .join(', ')}
            >
              <Tag color="orange">변경 {row.changeCount}건</Tag>
            </Tooltip>
          ) : (
            <Tag color="green">변경 없음</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '상태',
      key: 'status',
      width: 110,
      render: (_, row) => {
        if (row.active) return <Tag color="red">진행 중</Tag>;
        if (row.endReason === 'MANUAL') return <Tag color="default">정상 종료</Tag>;
        if (row.endedAt) return <Tag color="default">{row.endReason ?? '종료'}</Tag>;
        return <Tag color="default">시간 만료</Tag>;
      },
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {accessAllowed === false ? (
        <Alert
          type="success"
          showIcon
          message="지원 모드 접근을 차단해 두었습니다"
          description="플랫폼 운영자가 이 기업의 데이터에 접근할 수 없습니다. 장애 조치나 문의 대응이 필요할 때는 보안 정책에서 일시적으로 허용해 주셔야 합니다."
        />
      ) : (
        <Alert
          type="info"
          showIcon
          icon={<SafetyCertificateOutlined />}
          message="플랫폼 운영자의 접근은 이렇게 통제됩니다"
          description={
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              <li>사유를 입력해야 시작되고, 시간이 지나면 자동으로 끊깁니다(최대 4시간).</li>
              <li>세션 중의 모든 조회와 변경이 빠짐없이 기록되며, 이 화면에 그대로 표시됩니다.</li>
              <li>
                접근 자체를 원하지 않으시면 <b>보안 정책 → 지원 모드 접근 허용</b>을 꺼두실 수
                있습니다. 그러면 운영자 권한으로도 접근할 수 없습니다.
              </li>
            </ul>
          }
        />
      )}

      {activeCount > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`지금 ${activeCount}건의 지원 접근이 진행 중입니다`}
          description="예상하지 못한 접근이라면 보안 정책에서 지원 모드 접근 허용을 끄고 문의해 주세요."
        />
      )}

      {history.isError && <Alert type="error" showIcon message={errorText(history.error)} />}

      <Card
        size="small"
        title={`지원 접근 이력 (최근 ${rows.length}건)`}
        extra={
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => void client.invalidateQueries({ queryKey: qk.supportAccess })}
          >
            새로고침
          </Button>
        }
      >
        <Table<SupportAccessRecord>
          rowKey="supportSessionId"
          columns={columns}
          dataSource={rows}
          loading={history.isLoading}
          size="small"
          pagination={rows.length > 10 ? { pageSize: 10 } : false}
          expandable={{
            expandedRowRender: (row) => (
              <Space direction="vertical" size={4} style={{ paddingLeft: 8 }}>
                <Text strong style={{ fontSize: 12 }}>
                  세션 중 수행한 작업
                </Text>
                {row.activities.length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    기록된 작업이 없습니다.
                  </Text>
                ) : (
                  <Space size={4} wrap>
                    {row.activities.map((a) => (
                      <Tag key={a.action} color={a.isChange ? 'orange' : undefined}>
                        {a.label} {a.count}건
                      </Tag>
                    ))}
                  </Space>
                )}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  세션 ID: {row.supportSessionId}
                </Text>
              </Space>
            ),
            rowExpandable: (row) => row.activities.length > 0,
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Space direction="vertical" size={2}>
                    <Text>플랫폼 운영자가 이 기업의 데이터에 접근한 적이 없습니다.</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      접근이 발생하면 사유와 함께 여기에 기록됩니다.
                    </Text>
                  </Space>
                }
              />
            ),
          }}
        />
      </Card>

      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
        접근 기록은 변경하거나 삭제할 수 없습니다. 세부 감사로그는 3년간 보관한 뒤
        별도 보관소로 옮겨지며, 그 이후에도 접근 일시·담당자·사유는 이 목록에 남습니다.
      </Paragraph>
    </Space>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
}

/** 실제로 얼마나 머물렀는지. 허용 시간이 아니라 사용 시간을 보여준다. */
function duration(row: SupportAccessRecord): string {
  const end = row.endedAt ? new Date(row.endedAt).getTime() : Date.now();
  const minutes = Math.max(1, Math.round((end - new Date(row.startedAt).getTime()) / 60_000));
  if (minutes < 60) return `${minutes}분`;
  return `${Math.floor(minutes / 60)}시간 ${minutes % 60}분`;
}
